/**
 * Raport zarządu — teksty wspólnego wyglądu (`mbr.common.hero|tabBar|layout|tile|chart|insights|narrative|checksPanel.*`).
 * Plik FUNDAMENTU (zestaw komponentów features/mbr/shared); wkładany do `mbr.common` w mbr-common.ts.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const mbrKitPl = {
  hero: {
    compareChip: 'porównanie: {compare}',
    dataAsOf: 'dane z {date}',
    frozenAsOf: 'zamrożone {date}',
    controls: 'Okres i porównanie',
    stats: {
      sales: 'Sprzedaż po korektach',
      operating: 'Wynik operacyjny',
      bep: 'Dystans do progu',
      close: 'Stan zamknięcia',
    },
    base: 'bazowo {value}',
    bepPct: '{pct} progu · próg {bep}',
    bepAbove: 'powyżej progu · {pct} progu',
    closeForms: 'formularze {done}/{total}',
    closeChecks: '{fail} rozbieżności · {warn} do sprawdzenia',
    closeChecksOk: 'uzgodnienia bez rozbieżności',
    closeClosed: 'miesiąc zamknięty',
    actions: {
      close: 'Zamknięcie miesiąca',
      pdf: 'PDF',
      excel: 'Excel',
      send: 'Wyślij do zarządu',
    },
    insightOpen: 'Zobacz',
  },
  tabBar: {
    label: 'Zakładki raportu',
    select: 'Wybierz zakładkę raportu',
    fail: '{count} rozbieżności',
    warn: '{count} do sprawdzenia',
  },
  layout: {
    live: 'Liczone na żywo',
    frozen: 'Zamrożone przy zamknięciu',
    checks: 'Uzgodnienia: {fail} / {warn}',
    checksOk: 'Uzgodnienia OK',
    noKpis: 'Brak wskaźników dla tego okresu.',
    aiSummary: 'Podsumowanie AI',
  },
  tile: {
    definition: 'Co oznacza ten wskaźnik',
    alert: 'Uwaga — uzgodnienie danych',
    perDay: '{value} na dzień dostaw',
  },
  chart: {
    empty: 'Brak danych do wykresu w tym okresie.',
  },
  insights: {
    title: 'Wnioski AI',
    subtitle: 'Najważniejsze odchylenia miesiąca — liczby wyłącznie z danych raportu',
    recommendations: 'Rekomendacje na zarząd',
    filterAll: 'Wszystkie',
    severity: { HIGH: 'Pilne', MEDIUM: 'Ważne', LOW: 'Do wiadomości' },
    kind: {
      ANOMALY: 'Anomalia',
      TREND: 'Trend',
      RISK: 'Ryzyko',
      OPPORTUNITY: 'Szansa',
      DRIVER: 'Przyczyna',
      DATA_QUALITY: 'Jakość danych',
    },
    confidence: { HIGH: 'pewność wysoka', MEDIUM: 'pewność średnia', LOW: 'pewność niska' },
    source: { RULE: 'Reguła', AI: 'AI' },
    sourceHint: {
      RULE: 'Wniosek z reguły liczonej na danych raportu',
      AI: 'Wniosek modelu AI — liczby wyłącznie z faktów raportu',
    },
    impact: 'wpływ {value}',
    impactLabel: 'kwota w grze',
    perMonth: 'szansa / mies.',
    expand: 'więcej',
    collapse: 'mniej',
    facts: 'Liczby wniosku',
    refresh: 'Odśwież analizę',
    refreshed: 'Analiza odświeżona.',
    refreshError: 'Nie udało się odświeżyć analizy.',
    emptyTitle: 'Bez istotnych odchyleń',
    emptyHint: 'W tym okresie nic nie wymaga uwagi zarządu.',
    emptyFilter: 'Brak wniosków o tej wadze.',
    fallback: 'Wnioski z reguł pulpitu — analiza AI pojawi się po jej włączeniu.',
    dataGaps: 'Braki danych: {list}',
    openTab: 'Otwórz: {tab}',
    current: 'bieżący',
    base: 'bazowo',
    more: 'Pokaż wszystkie ({count})',
    less: 'Zwiń',
    task: {
      title: 'Utworzyć zadanie?',
      body: 'Zadanie trafi na Twoją listę zadań z terminem na jutro.',
      name: 'Tytuł zadania',
      create: 'Utwórz zadanie',
      cancel: 'Anuluj',
      created: 'Zadanie utworzone.',
      error: 'Nie udało się utworzyć zadania.',
      description: 'Z raportu zarządu ({period}): {body}',
    },
  },
  narrative: {
    title: 'Komentarz zarządczy',
    subtitle: 'Podsumowanie miesiąca i rekomendacje — liczby wyłącznie z faktów raportu',
    draft: 'wersja robocza',
    draftHint: 'Miesiąc jest otwarty — komentarz zmieni się po zamknięciu i zamrożeniu danych.',
    mode: { LIVE: 'Model AI', MOCK: 'Tryb demo', TEMPLATE: 'Szablon z faktów' },
    generatedAt: 'wygenerowano {date}',
    changes: 'Co się zmieniło',
    recommendations: 'Rekomendacje',
    more: 'Pokaż wszystko ({count})',
    less: 'Zwiń',
    emptyTitle: 'Komentarza jeszcze nie ma',
    emptyOpen: 'Dla otwartego miesiąca komentarz powstaje na żądanie, jako wersja robocza.',
    emptyClosed: 'Komentarz powstaje przy zamknięciu miesiąca.',
    generate: 'Wygeneruj teraz',
    regenerate: 'Wygeneruj ponownie',
    generated: 'Komentarz wygenerowany.',
    error: 'Nie udało się wygenerować komentarza.',
  },
  checksPanel: {
    group: {
      FAIL: 'Czerwone — do poprawy',
      WARN: 'Żółte — do sprawdzenia',
      INFO: 'Informacje',
      OK: 'Zgodne',
    },
    title: 'Uzgodnienia danych',
    counts: '{fail} rozbieżności · {warn} do sprawdzenia · {ok} OK',
    allOk: 'Wszystkie uzgodnienia zgodne ({ok})',
    show: 'Pokaż uzgodnienia',
    hide: 'Zwiń uzgodnienia',
    go: 'Przejdź',
    showOk: 'Pokaż też zgodne',
    hideOk: 'Ukryj zgodne',
  },
  ai: 'AI',
};

export const mbrKitEn: Shape<typeof mbrKitPl> = {
  hero: {
    compareChip: 'comparison: {compare}',
    dataAsOf: 'data as of {date}',
    frozenAsOf: 'frozen {date}',
    controls: 'Period and comparison',
    stats: {
      sales: 'Net sales after corrections',
      operating: 'Operating result',
      bep: 'Distance to break-even',
      close: 'Close status',
    },
    base: 'base {value}',
    bepPct: '{pct} of break-even · BEP {bep}',
    bepAbove: 'above break-even · {pct} of BEP',
    closeForms: 'forms {done}/{total}',
    closeChecks: '{fail} discrepancies · {warn} to check',
    closeChecksOk: 'no reconciliation discrepancies',
    closeClosed: 'month closed',
    actions: {
      close: 'Month close',
      pdf: 'PDF',
      excel: 'Excel',
      send: 'Send to the board',
    },
    insightOpen: 'Open',
  },
  tabBar: {
    label: 'Report tabs',
    select: 'Choose a report tab',
    fail: '{count} discrepancies',
    warn: '{count} to check',
  },
  layout: {
    live: 'Computed live',
    frozen: 'Frozen at close',
    checks: 'Checks: {fail} / {warn}',
    checksOk: 'Checks OK',
    noKpis: 'No indicators for this period.',
    aiSummary: 'AI summary',
  },
  tile: {
    definition: 'What this indicator means',
    alert: 'Attention — data reconciliation',
    perDay: '{value} per delivery day',
  },
  chart: {
    empty: 'No chart data for this period.',
  },
  insights: {
    title: 'AI insights',
    subtitle: 'The month’s key deviations — numbers only from report data',
    recommendations: 'Recommendations for the board',
    filterAll: 'All',
    severity: { HIGH: 'Urgent', MEDIUM: 'Important', LOW: 'For information' },
    kind: {
      ANOMALY: 'Anomaly',
      TREND: 'Trend',
      RISK: 'Risk',
      OPPORTUNITY: 'Opportunity',
      DRIVER: 'Driver',
      DATA_QUALITY: 'Data quality',
    },
    confidence: { HIGH: 'high confidence', MEDIUM: 'medium confidence', LOW: 'low confidence' },
    source: { RULE: 'Rule', AI: 'AI' },
    sourceHint: {
      RULE: 'Finding from a rule computed on report data',
      AI: 'AI model finding — numbers only from report facts',
    },
    impact: 'impact {value}',
    impactLabel: 'at stake',
    perMonth: 'upside / month',
    expand: 'more',
    collapse: 'less',
    facts: 'Insight figures',
    refresh: 'Refresh analysis',
    refreshed: 'Analysis refreshed.',
    refreshError: 'Could not refresh the analysis.',
    emptyTitle: 'No material deviations',
    emptyHint: 'Nothing in this period needs the board’s attention.',
    emptyFilter: 'No insights of this weight.',
    fallback: 'Findings from dashboard rules — AI analysis appears once enabled.',
    dataGaps: 'Data gaps: {list}',
    openTab: 'Open: {tab}',
    current: 'current',
    base: 'base',
    more: 'Show all ({count})',
    less: 'Collapse',
    task: {
      title: 'Create a task?',
      body: 'The task goes to your task list, due tomorrow.',
      name: 'Task title',
      create: 'Create task',
      cancel: 'Cancel',
      created: 'Task created.',
      error: 'Could not create the task.',
      description: 'From the management report ({period}): {body}',
    },
  },
  narrative: {
    title: 'Management commentary',
    subtitle: 'Month summary and recommendations — numbers only from report facts',
    draft: 'draft',
    draftHint: 'The month is open — the commentary will change once it is closed and the data frozen.',
    mode: { LIVE: 'AI model', MOCK: 'Demo mode', TEMPLATE: 'Template from facts' },
    generatedAt: 'generated {date}',
    changes: 'What changed',
    recommendations: 'Recommendations',
    more: 'Show all ({count})',
    less: 'Collapse',
    emptyTitle: 'No commentary yet',
    emptyOpen: 'For an open month the commentary is generated on demand, as a draft.',
    emptyClosed: 'The commentary is generated when the month is closed.',
    generate: 'Generate now',
    regenerate: 'Generate again',
    generated: 'Commentary generated.',
    error: 'Could not generate the commentary.',
  },
  checksPanel: {
    group: { FAIL: 'Red — must be fixed', WARN: 'Amber — to check', INFO: 'For information', OK: 'OK' },
    title: 'Data reconciliation',
    counts: '{fail} discrepancies · {warn} to check · {ok} OK',
    allOk: 'All checks pass ({ok})',
    show: 'Show checks',
    hide: 'Collapse checks',
    go: 'Open',
    showOk: 'Show passing too',
    hideOk: 'Hide passing',
  },
  ai: 'AI',
};
